import { z } from 'zod';

import { DestinationImageSourceSchema } from '../traveler/destination_image_view';
import { ApprovalPendingMomentSchema } from './approval_pending_items';
import { TripCalendarEventSchema } from './trip_calendar_event';

/**
 * `trip_update` (#3765, Projeto A): ONE message per trip and traveler with the
 * final outcome of each item plus what is still pending. voucher-events builds
 * this language-neutral photo; the conversation localizes it into vars and each
 * connector lays the vars out.
 */
export const TripOutcomeSchema = z.enum(['emitted', 'canceled', 'expired', 'rejected']);
export type TripOutcome = z.infer<typeof TripOutcomeSchema>;

export const TripItemStateSchema = z.enum([
  'emitted',
  'canceled',
  'expired',
  'rejected',
  'awaiting_approval',
  'awaiting_emission',
]);
export type TripItemState = z.infer<typeof TripItemStateSchema>;

/** The service of an item, finer than `modal`: the e-mail names and draws each one. */
export const TripServiceSchema = z.enum([
  'flight',
  'hotel',
  'car',
  'bus',
  'mobility',
  'concierge',
  'other',
]);
export type TripService = z.infer<typeof TripServiceSchema>;

export const TripUpdateItemSchema = z.object({
  voucherId: z.string().min(1),
  modal: z.enum(['flight', 'hotel', 'car', 'other']),
  /** Flight only: "GRU → LAX". */
  route: z.string().nullable(),
  direction: z.enum(['outbound', 'return']).nullable(),
  /** Hotel name, car model or supplier; null for flights. */
  name: z.string().nullable(),
  /** Flight carrier name. */
  carrier: z.string().nullable(),
  /** Offset-less wall clock `YYYY-MM-DDTHH:mm[:ss]`: departure or check-in. */
  startLocal: z.string().nullable(),
  /** Offset-less wall clock of the end (check-out); null for flights. */
  endLocal: z.string().nullable(),
  state: TripItemStateSchema,
  /** Airline locator (flight) or supplier confirmation (hotel/car). */
  code: z.string().nullable(),
  /** The item reached its outcome inside THIS window. */
  changed: z.boolean(),
  /** Rejection justification typed by the approver (platform decisions only). PII-free text. */
  justification: z.string().nullable(),
  /** An approval existed before the outcome: an expiry after approval reads differently. */
  approvedBefore: z.boolean(),
  // Boarding-card e-mail (spec 2026-10-04): what a flight item's card shows.
  // Optional: voucher-events and the conversation deploy separately, and a
  // non-flight item never carries them.
  /** IATA of the leg's first boarding and last landing. */
  from: z.string().nullable().optional(),
  to: z.string().nullable().optional(),
  /** The leg's flight numbers, in order ("3302"; "227", "759" across a connection). */
  flightNumbers: z.array(z.string()).optional(),
  /** Offset-less wall clock of the leg's last landing. */
  arriveLocal: z.string().nullable().optional(),
  /** Stops between the first boarding and the last landing. */
  stops: z.number().int().min(0).nullable().optional(),
  /** Real elapsed minutes from the first departure to the last arrival. */
  durationMinutes: z.number().int().positive().nullable().optional(),
  // Trip e-mail by day (#7098): the service and what each card shows. Optional and
  // additive, like the flight fields: `modal` keeps its four values (an older
  // conversation rejects a fifth), so a bus, a ride or a concierge request travels as
  // `modal: 'other'` and names itself here. A bus reuses `arriveLocal` and
  // `durationMinutes`.
  service: TripServiceSchema.optional(),
  /** Hotel: the city of the stay. */
  hotelCity: z.string().nullable().optional(),
  /** Car: the rental company, the pick-up agency, the daily rates and the damage protection. */
  rentalCompany: z.string().nullable().optional(),
  pickupAgency: z.string().nullable().optional(),
  rentalDays: z.number().int().positive().nullable().optional(),
  protectionIncluded: z.boolean().optional(),
  /** Bus: the cities of the first and last stations, the operator and the seat class. */
  busFromCity: z.string().nullable().optional(),
  busToCity: z.string().nullable().optional(),
  busCompany: z.string().nullable().optional(),
  busClass: z.string().nullable().optional(),
  /** Ride: the places the traveler picked and the ride category. */
  rideOrigin: z.string().nullable().optional(),
  rideDestination: z.string().nullable().optional(),
  rideCategory: z.string().nullable().optional(),
});
export type TripUpdateItem = z.infer<typeof TripUpdateItemSchema>;

/** The raw `voucher.finished` of one emitted voucher, from which the e-mail's PDF is printed. */
export const TripVoucherDocumentSchema = z.object({
  voucherId: z.string().min(1),
  voucherCode: z.string().min(1),
  document: z.record(z.string(), z.unknown()),
});
export type TripVoucherDocument = z.infer<typeof TripVoucherDocumentSchema>;

export const TripUpdateMetadataSchema = z.object({
  tripId: z.string().min(1),
  destination: z.string().nullable(),
  startLocal: z.string().nullable(),
  endLocal: z.string().nullable(),
  /** Set only on the copy sent to whoever booked FOR the traveler (D15). PII. */
  travelerName: z.string().nullable(),
  /**
   * Who this copy is for (B1, #3765): the traveler's own copy or the one sent to
   * whoever booked for them — it picks the preference row (`trip_update` /
   * `requested_trip_update`). Optional: an older producer omits it and the
   * conversation falls back to `travelerName`.
   */
  recipientRole: z.enum(['traveler', 'requester']).optional(),
  /**
   * The purchase origin (`voucher.channel`) for the conversation's e-mail rule: the platform only
   * when every outcome of this copy is a platform purchase (a platform denial of a legacy voucher
   * carries that voucher's origin). Optional: an older producer omits it.
   */
  originChannel: z.string().nullable().optional(),
  /**
   * The platform share of a copy that MIXES platform outcomes with legacy or unknown-origin ones
   * (#6478, as `PLATFORM_SHARE_KEYS` for an approval): `items` without the changes that are not
   * the platform's. The legacy notifier e-mails those and is silent on the platform ones, so this
   * is all the e-mail shows; the app inbox and the other channels keep `items`. Absent on a copy
   * that is not mixed.
   */
  platformItems: z.array(TripUpdateItemSchema).min(1).optional(),
  /**
   * The Admin's "Reenviar" (#5911): a copy of one emitted voucher for whoever clicked. The
   * conversation sends it by e-mail only, as a channel copy (no app inbox, grid or quiet hours).
   */
  resend: z.literal(true).optional(),
  /** Expiry deadline (ISO instant) when an item expired in this window. */
  deadlineAt: z.string().nullable(),
  /** The trip's destination photo (the app cover) and what its credit needs. */
  image: z
    .object({
      url: z.string().min(1),
      photographer: z.string(),
      photographerUrl: z.string(),
      source: DestinationImageSourceSchema.optional(),
      license: z.string().optional(),
      licenseUrl: z.string().optional(),
      sourceUrl: z.string().optional(),
    })
    .nullable()
    .optional(),
  /**
   * The emitted reservations as calendar events (schema.org markup and `.ics` of the e-mail).
   * Optional: voucher-events and the conversation deploy separately. A malformed calendar reads
   * as absent, so it never makes the whole notice unsendable.
   */
  calendar: z.array(TripCalendarEventSchema).optional().catch(undefined),
  /** Only on the traveler's own copy: the traveler's full name, for the reservation's `underName`. PII. */
  travelerFullName: z.string().nullable().optional(),
  /**
   * One per voucher emitted in this copy, for the voucher PDF the e-mail attaches. PII. Optional:
   * a voucher over the size budget is left out, and a malformed list reads as absent.
   */
  voucherDocuments: z
    .unknown()
    .transform((value) => TripVoucherDocumentSchema.array().safeParse(value).data)
    .optional(),
  items: z.array(TripUpdateItemSchema).min(1),
});
export type TripUpdateMetadata = z.infer<typeof TripUpdateMetadataSchema>;

/** What the e-mail card shows for one item, already localized by the conversation. */
export const TripUpdateCardSchema = z.object({
  /** "Ida · LATAM 3302 · sáb 7 nov", "Hotel · 2 noites". */
  header: z.string(),
  /** Flight only: the two airports with their clocks. */
  route: z
    .object({
      from: z.string(),
      fromTime: z.string().nullable(),
      to: z.string(),
      toTime: z.string().nullable(),
      /** Days the landing falls after the departure day ("+1"). */
      toDayOffset: z.number().int().min(0),
      /** "direto · 1h15". */
      meta: z.string().nullable(),
    })
    .optional(),
  /** Hotel name or car model: the card's big line. */
  name: z.string().optional(),
  facts: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      mono: z.boolean().optional(),
      /** The value no longer works (a canceled locator). */
      void: z.boolean().optional(),
    }),
  ),
});
export type TripUpdateCard = z.infer<typeof TripUpdateCardSchema>;

/**
 * The card of one item in the trip e-mail by day (#7098), localized by the conversation: the
 * service ("Voo", "Hospedagem"), what it is, the detail lines, the two time columns and the
 * notes (arrival on another day, the approver's reason). The state, the code and the strike
 * come from the display item itself.
 */
export const TripUpdateSheetCardSchema = z.object({
  kind: z.string(),
  /** The state badge ("Em aprovação", "Cancelado"); absent on an issued item. */
  badge: z.string().optional(),
  title: z.string(),
  sub: z.array(z.string()),
  /** The landing day's offset ("+1") rides in `plus`. */
  moments: z.array(ApprovalPendingMomentSchema.extend({ plus: z.string().optional() })).max(2),
  notes: z.array(z.object({ kind: z.enum(['info', 'motivo']), text: z.string() })),
  /** Square airline mark (https), flights only. */
  thumbUrl: z.string().optional(),
});
export type TripUpdateSheetCard = z.infer<typeof TripUpdateSheetCardSchema>;

/**
 * One item as the connectors render it: localized by the conversation and
 * shipped as the `itemsJson` var (vars are strings), so Slack and e-mail lay
 * out the same words the WhatsApp line carries.
 */
export const TripUpdateDisplayItemSchema = z.object({
  title: z.string(),
  detail: z.string().nullable(),
  stateLabel: z.string(),
  tone: z.enum(['ok', 'wait', 'bad']),
  codeLabel: z.string().nullable(),
  code: z.string().nullable(),
  /** The code no longer works (canceled item). */
  void: z.boolean(),
  reason: z.string().nullable(),
  /** The item reached its outcome in THIS window ("O que mudou"); absent from older producers. */
  changed: z.boolean().optional(),
  card: TripUpdateCardSchema.optional(),
  // Trip e-mail by day (#7098). Optional: an older conversation sends none, and the e-mail
  // keeps the layout of today.
  service: TripServiceSchema.optional(),
  /** The start's wall-clock day (`YYYY-MM-DD`) and its label ("Qui, 12 de novembro"). */
  dayKey: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  dayLabel: z.string().optional(),
  sheet: TripUpdateSheetCardSchema.optional(),
});
export type TripUpdateDisplayItem = z.infer<typeof TripUpdateDisplayItemSchema>;
export const TripUpdateDisplayItemsSchema = z.array(TripUpdateDisplayItemSchema).min(1);

/** Parses the `itemsJson` var; null when absent, unreadable or empty. */
export function parseTripUpdateDisplayItems(
  raw: string | undefined,
): TripUpdateDisplayItem[] | null {
  if (raw === undefined) return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const parsed = TripUpdateDisplayItemsSchema.safeParse(json);
  return parsed.success ? parsed.data : null;
}
